import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, EnvelopeSimple, CheckCircle } from '@phosphor-icons/react';
import api from '../services/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setSubmitted(true);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        toast.error('Too many attempts. Please wait a minute before trying again.');
      } else {
        // Intentionally generic to avoid enumeration — backend also anonymizes
        setSubmitted(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
          data-testid="back-to-login-link"
        >
          <ArrowLeft size={16} /> Back to sign in
        </Link>

        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                <EnvelopeSimple size={22} className="text-indigo-700" weight="regular" />
              </div>
              <div>
                <CardTitle className="text-xl" data-testid="forgot-password-title">Forgot your password?</CardTitle>
                <CardDescription className="text-sm">
                  We'll email you a link to choose a new one.
                </CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {submitted ? (
              <div
                className="text-center py-4"
                data-testid="forgot-password-success"
              >
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle size={28} className="text-emerald-600" weight="fill" />
                </div>
                <h3 className="text-base font-semibold text-slate-900 mb-1">
                  Check your inbox
                </h3>
                <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                  If an account exists for <strong>{email}</strong>, a password reset link has
                  been sent. The link is valid for 60 minutes.
                </p>
                <p className="text-xs text-slate-400 mb-5">
                  Didn't get the email? Check your spam folder, or try again in a minute.
                </p>
                <div className="flex gap-2 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSubmitted(false);
                      setEmail('');
                    }}
                    data-testid="send-another-btn"
                  >
                    Try another email
                  </Button>
                  <Link to="/login">
                    <Button data-testid="go-to-login-btn">Back to sign in</Button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-slate-700">
                    Your email
                  </Label>
                  <div className="relative mt-1">
                    <EnvelopeSimple
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="name@agency.com"
                      required
                      autoFocus
                      className="pl-10"
                      data-testid="forgot-password-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-indigo-900 hover:bg-indigo-800 h-11"
                  data-testid="forgot-password-submit-btn"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    'Send reset link'
                  )}
                </Button>

                <p className="text-xs text-slate-400 text-center pt-2">
                  For security, we'll send the same confirmation whether or not the email is registered.
                </p>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
